import React, { useEffect, useRef } from 'react';

class Data {
  x: number;
  y: number;
  history: string[];
  historySizeMax: number;
  constructor(x: number, config: any) {
    this.x = x;
    this.y = 0;
    this.history = [];
    this.historySizeMax = Math.floor(Math.random() * config.maxLength + config.minLength);
  }
  update(height: number, config: any, datarray: Data[], putData: (x?: number) => void) {
    this.y += config.size;
    if (this.y >= height + this.historySizeMax * config.size) {
      datarray.splice(datarray.indexOf(this), 1);
      putData(this.x);
    }
    this.history.unshift(String.fromCharCode(60 + Math.floor(Math.random() * 62)));
    if (this.history.length > this.historySizeMax) this.history.pop();
  }
  draw(ctx: CanvasRenderingContext2D, config: any) {
    if (Math.random() > 0.995) return;
    ctx.fillStyle = config.firstColor;
    ctx.fillText(this.history[0], this.x, this.y);
    ctx.fillStyle = config.color;
    for (let i = 1, char; (char = this.history[i]); i++) {
      ctx.fillText(char, this.x, this.y - i * config.size);
    }
  }
}

// Definir colores para cada sección
const sectionColors = {
  home: { firstColor: '#888', color: '#444' },
  features: { firstColor: '#666', color: '#333' },
  'how-it-works': { firstColor: '#555', color: '#222' },
  cryptocurrencies: { firstColor: '#777', color: '#333' },
  security: { firstColor: '#444', color: '#111' },
  pricing: { firstColor: '#666', color: '#222' },
  testimonials: { firstColor: '#555', color: '#222' },
  faq: { firstColor: '#777', color: '#333' },
  default: { firstColor: '#888', color: '#444' }
};

const MatrixRainBg: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colorRef = useRef(sectionColors.home);
  const datarrayRef = useRef<Data[]>([]);
  const configRef = useRef<any>({});
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const getCurrentSection = () => {
      const sections = [
        'home', 'features', 'how-it-works', 'cryptocurrencies', 
        'security', 'pricing', 'testimonials', 'faq'
      ];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            return section;
          }
        }
      }
      return 'home';
    };

    const config = {
      amount: 70,
      speed: 100,
      size: 15,
      minLength: 5,
      maxLength: 11,
      ...sectionColors.home
    };
    configRef.current = config;
    colorRef.current = sectionColors.home;

    let datarray: Data[] = [];
    datarrayRef.current = datarray;
    let count = Math.floor(width / config.size);

    function putData(x?: number) {
      const newX = typeof x === 'number' ? x : Math.floor(Math.random() * count) * config.size;
      for (let i = 0, row; (row = datarrayRef.current[i]); i++) {
        if (row.x === newX && row.y - row.historySizeMax * config.size + config.size < config.size) return;
      }
      datarrayRef.current.push(new Data(newX, configRef.current));
    }

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      
      // Actualizar colores dinámicamente según la sección visible
      const section = getCurrentSection();
      const currentColors = sectionColors[section as keyof typeof sectionColors] || sectionColors.default;
      colorRef.current = currentColors;
      configRef.current.firstColor = currentColors.firstColor;
      configRef.current.color = currentColors.color;
      
      if (datarrayRef.current.length < configRef.current.amount) putData();
      for (let i = 0, column; (column = datarrayRef.current[i]); i++) {
        column.update(height, configRef.current, datarrayRef.current, putData);
        column.draw(ctx, configRef.current);
      }
      animationFrameIdRef.current = window.setTimeout(draw, configRef.current.speed);
    }

    ctx.font = config.size + 'px monospace';
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.shadowBlur = 6;
    ctx.shadowColor = config.color;

    draw();

    // Solo actualizar tamaño, nunca reiniciar la animación ni los datos
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      count = Math.floor(width / config.size);
      // No reiniciar datarrayRef.current
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', () => {}); // Para que getCurrentSection funcione

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameIdRef.current !== null) {
        window.clearTimeout(animationFrameIdRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="matrix"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        display: 'block',
        pointerEvents: 'none',
      }}
    />
  );
};

export default MatrixRainBg; 